// src/pages/Admin/SystemLogPage.jsx
// Real-time system log viewer — shows config changes, cache reloads,
// call uploads, and API activity across all dynamic scenarios.
// Route: /admin/system-log

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Select, Input, Tag, Spin, Tooltip, message as antMsg } from 'antd';
import {
  ReloadOutlined, SearchOutlined, DownloadOutlined,
  CheckCircleOutlined, WarningOutlined, InfoCircleOutlined,
  CloseCircleOutlined, FilterOutlined, ClearOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

// ── Design tokens ────────────────────────────────────────────────
const C = {
  navy:'#1B2D6B', blue:'#1E6FBF', lblue:'#EAF1F8',
  green:'#16a34a', lgreen:'#F0FDF4',
  red:'#DC2626',   lred:'#FEF2F2',
  amber:'#B45309', lamber:'#FFFBEB',
  teal:'#0891b2',  lteal:'#ecfeff',
  purple:'#7c3aed',lpurple:'#f5f3ff',
  text1:'#1a2d4a', text2:'#4a6580', text3:'#7a96b0',
  border:'#DCE6F0', surf:'#F5F8FC', white:'#FFFFFF',
};

// ── Log level config ─────────────────────────────────────────────
const LEVEL = {
  INFO:    { color:C.blue,   bg:C.lblue,   icon:<InfoCircleOutlined/>,    label:'INFO'    },
  SUCCESS: { color:C.green,  bg:C.lgreen,  icon:<CheckCircleOutlined/>,   label:'SUCCESS' },
  WARNING: { color:C.amber,  bg:C.lamber,  icon:<WarningOutlined/>,       label:'WARN'    },
  ERROR:   { color:C.red,    bg:C.lred,    icon:<CloseCircleOutlined/>,   label:'ERROR'   },
};

// ── Event categories ─────────────────────────────────────────────
const CATEGORY = {
  CONFIG:     { label:'⚙️ Config Change',   color:C.purple, bg:C.lpurple },
  CACHE:      { label:'🔄 Cache Reload',    color:C.teal,   bg:C.lteal   },
  UPLOAD:     { label:'📤 Call Upload',     color:C.blue,   bg:C.lblue   },
  EXPORT:     { label:'📊 Report Export',   color:C.green,  bg:C.lgreen  },
  SCHEDULE:   { label:'📅 Schedule',        color:C.navy,   bg:'#eff6ff' },
  QUESTIONS:  { label:'❓ Questions',       color:C.amber,  bg:C.lamber  },
  AUTH:       { label:'🔐 Auth',            color:C.text3,  bg:C.surf    },
};

// ── Persistent log store — localStorage backed ───────────────────
// Logs survive navigation and page refreshes within the same browser session.
const LS_KEY = 'ejt_system_logs';
const MAX_LOGS = 500;

function readLogs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writeLogs(logs) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(logs.slice(0, MAX_LOGS)));
  } catch { /* storage full — ignore */ }
}

export function addLog(category, level, message, detail = null) {
  const entry = {
    id:        `${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
    timestamp: new Date().toISOString(),
    category,
    level,
    message,
    detail,
    source:    'frontend',
  };
  const existing = readLogs();
  writeLogs([entry, ...existing]);
  // Dispatch event so open log page updates live if mounted
  window.dispatchEvent(new CustomEvent('system-log', { detail: entry }));
  return entry;
}

export function clearLogs() {
  localStorage.removeItem(LS_KEY);
  window.dispatchEvent(new CustomEvent('system-log-clear'));
}

// ══════════════════════════════════════════════════════════════════
export default function SystemLogPage() {
  const [logs,        setLogs]        = useState([]);
  const [backendLogs, setBackendLogs] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [filterCat,   setFilterCat]   = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [search,      setSearch]      = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showDetail,  setShowDetail]  = useState(null);
  const intervalRef = useRef(null);

  // ── Merge session + backend logs ─────────────────────────────
  const allLogs = [...readLogs(), ...backendLogs]
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 500);

  // ── Fetch backend audit log ───────────────────────────────────
  const fetchBackendLogs = useCallback(async () => {
    try {
      const res = await api.get('/admin/logs');
      const rows = Array.isArray(res.data?.data) ? res.data.data
        : Array.isArray(res.data) ? res.data : [];
      setBackendLogs(rows);
    } catch {
      // Backend log endpoint optional — session logs always work
    }
  }, []);

  useEffect(() => {
    setLogs(readLogs());
    fetchBackendLogs();
  }, [fetchBackendLogs]);

  // ── Live update when new log event fires ─────────────────────
  useEffect(() => {
    const handler = () => setLogs(readLogs());
    window.addEventListener('system-log', handler);
    return () => window.removeEventListener('system-log', handler);
  }, []);

  // ── Auto-refresh every 10s ────────────────────────────────────
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        setLogs(readLogs());
        fetchBackendLogs();
      }, 10000);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchBackendLogs]);

  // ── Filter ────────────────────────────────────────────────────
  const filtered = allLogs.filter(log => {
    if (filterCat !== 'all' && log.category !== filterCat) return false;
    if (filterLevel !== 'all' && log.level !== filterLevel) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(log.message||'').toLowerCase().includes(s) &&
          !(log.category||'').toLowerCase().includes(s) &&
          !(JSON.stringify(log.detail||'')).toLowerCase().includes(s)) return false;
    }
    return true;
  });

  // ── Stats ─────────────────────────────────────────────────────
  const localLogs = readLogs();
  const stats = {
    total:   allLogs.length,
    errors:  allLogs.filter(l => l.level === 'ERROR').length,
    warnings:allLogs.filter(l => l.level === 'WARNING').length,
    config:  allLogs.filter(l => l.category === 'CONFIG').length,
    uploads: allLogs.filter(l => l.category === 'UPLOAD').length,
  };

  // ── Export logs to Excel ──────────────────────────────────────
  function exportLogs() {
    const rows = filtered.map(log => ({
      'Timestamp':  dayjs(log.timestamp).format('DD MMM YYYY HH:mm:ss'),
      'Category':   log.category,
      'Level':      log.level,
      'Message':    log.message,
      'Detail':     typeof log.detail === 'object' ? JSON.stringify(log.detail) : (log.detail||''),
      'Source':     log.source || 'frontend',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SystemLog');
    XLSX.writeFile(wb, `SystemLog_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`);
  }

  return (
    <div style={{ background:'#f0f4f8', minHeight:'100vh' }}>

      {/* ── Page header ── */}
      <div style={{
        background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        padding:'20px 28px',
        boxShadow:'0 4px 20px rgba(27,45,107,0.25)',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:'#fff',
              display:'flex', alignItems:'center', gap:10 }}>
              🗂️ System Log
            </div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginTop:3 }}>
              Config changes · Cache reloads · Call uploads · Report exports · Dynamic scenario audit
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Button
              icon={<ReloadOutlined spin={autoRefresh}/>}
              onClick={() => setAutoRefresh(p => !p)}
              style={{
                borderRadius:8,
                background: autoRefresh ? 'rgba(22,163,74,0.2)' : 'rgba(255,255,255,0.12)',
                border:`1px solid ${autoRefresh ? '#86efac' : 'rgba(255,255,255,0.25)'}`,
                color: autoRefresh ? '#86efac' : '#fff', fontWeight:600,
              }}>
              {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            </Button>
            <Button icon={<ReloadOutlined/>}
              onClick={() => { setLogs(readLogs()); fetchBackendLogs(); }}
              loading={loading}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)', color:'#fff' }}>
              Refresh
            </Button>
            <Button icon={<DownloadOutlined/>} onClick={exportLogs}
              style={{ borderRadius:8, background:'#fff', border:'none',
                color:C.navy, fontWeight:700 }}>
              Export
            </Button>
            <Button danger onClick={() => { clearLogs(); antMsg.success('Logs cleared'); }}
              style={{ borderRadius:8, background:'rgba(220,38,38,0.15)',
                border:'1px solid rgba(220,38,38,0.3)', color:'#fca5a5', fontWeight:600 }}>
              Clear All
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding:'24px 28px 60px' }}>

        {/* ── Stats strip ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)',
          gap:12, marginBottom:20 }}>
          {[
            { label:'Total Events',   value:stats.total,    color:C.navy,   bg:'#eff6ff',  icon:'📋' },
            { label:'Errors',         value:stats.errors,   color:C.red,    bg:C.lred,     icon:'❌' },
            { label:'Warnings',       value:stats.warnings, color:C.amber,  bg:C.lamber,   icon:'⚠️' },
            { label:'Config Changes', value:stats.config,   color:C.purple, bg:C.lpurple,  icon:'⚙️' },
            { label:'Call Uploads',   value:stats.uploads,  color:C.blue,   bg:C.lblue,    icon:'📤' },
          ].map((s,i) => (
            <div key={i} style={{
              background:s.bg, borderRadius:14,
              border:`1px solid ${s.color}22`,
              borderTop:`3px solid ${s.color}`,
              padding:'14px 16px',
              boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
            }}>
              <div style={{ fontSize:22, marginBottom:4 }}>{s.icon}</div>
              <div style={{ fontSize:24, fontWeight:800, color:s.color, lineHeight:1 }}>
                {s.value}
              </div>
              <div style={{ fontSize:10, fontWeight:700, color:C.text3,
                textTransform:'uppercase', letterSpacing:'0.5px', marginTop:4 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16,
          background:C.white, padding:'12px 16px', borderRadius:12,
          border:`1px solid ${C.border}`,
          boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
          <Input
            prefix={<SearchOutlined style={{ color:C.text3 }}/>}
            placeholder="Search logs…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ width:240, borderRadius:8 }}
          />
          <Select value={filterCat} onChange={setFilterCat} style={{ width:180 }}>
            <Select.Option value="all">All Categories</Select.Option>
            {Object.entries(CATEGORY).map(([k,v]) => (
              <Select.Option key={k} value={k}>{v.label}</Select.Option>
            ))}
          </Select>
          <Select value={filterLevel} onChange={setFilterLevel} style={{ width:140 }}>
            <Select.Option value="all">All Levels</Select.Option>
            {Object.entries(LEVEL).map(([k,v]) => (
              <Select.Option key={k} value={k}>{v.label}</Select.Option>
            ))}
          </Select>
          <span style={{ fontSize:12, color:C.text3, alignSelf:'center' }}>
            {filtered.length} / {allLogs.length} events
          </span>
          {(filterCat!=='all'||filterLevel!=='all'||search) && (
            <Button size="small" icon={<ClearOutlined/>}
              onClick={() => { setFilterCat('all'); setFilterLevel('all'); setSearch(''); }}
              style={{ borderRadius:8 }}>
              Clear Filters
            </Button>
          )}
        </div>

        {/* ── Log table ── */}
        <div style={{ background:C.white, borderRadius:14,
          border:`1px solid ${C.border}`,
          boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
          overflow:'hidden' }}>

          {/* Table header */}
          <div style={{
            display:'grid',
            gridTemplateColumns:'170px 120px 90px 1fr 80px',
            gap:0,
            background:C.navy, padding:'10px 16px',
          }}>
            {['Timestamp','Category','Level','Message / Detail','Source'].map(h => (
              <div key={h} style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,.7)',
                textTransform:'uppercase', letterSpacing:'0.5px' }}>
                {h}
              </div>
            ))}
          </div>

          {/* Log rows */}
          <div style={{ maxHeight:'60vh', overflowY:'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ textAlign:'center', padding:60, color:C.text3 }}>
                <div style={{ fontSize:32, marginBottom:12 }}>📭</div>
                <div style={{ fontSize:13 }}>
                  {allLogs.length === 0
                    ? 'No logs yet — save a schedule, upload calls, or reload the cache to see events here'
                    : 'No logs match your current filters — try clearing filters'}
                </div>
              </div>
            ) : filtered.map((log, i) => {
              const cat = CATEGORY[log.category] || { label:log.category, color:C.text3, bg:C.surf };
              const lvl = LEVEL[log.level] || LEVEL.INFO;
              const isEven = i % 2 === 0;
              return (
                <div key={log.id}
                  onClick={() => setShowDetail(showDetail?.id===log.id ? null : log)}
                  style={{
                    display:'grid',
                    gridTemplateColumns:'170px 120px 90px 1fr 80px',
                    gap:0, cursor:'pointer',
                    background: showDetail?.id===log.id
                      ? '#eff6ff'
                      : isEven ? C.white : '#fafcff',
                    borderBottom:`1px solid ${C.border}`,
                    transition:'background .1s',
                  }}
                  onMouseEnter={e => { if(showDetail?.id!==log.id) e.currentTarget.style.background='#f0f6ff'; }}
                  onMouseLeave={e => { if(showDetail?.id!==log.id) e.currentTarget.style.background=isEven?C.white:'#fafcff'; }}>

                  {/* Timestamp */}
                  <div style={{ padding:'10px 16px', fontSize:11,
                    color:C.text3, fontFamily:'monospace' }}>
                    <div style={{ fontWeight:600, color:C.text2 }}>
                      {dayjs(log.timestamp).format('DD MMM HH:mm:ss')}
                    </div>
                    <div style={{ fontSize:10, color:C.text3 }}>
                      {dayjs(log.timestamp).fromNow?.() || ''}
                    </div>
                  </div>

                  {/* Category */}
                  <div style={{ padding:'10px 8px', display:'flex', alignItems:'center' }}>
                    <Tag style={{
                      borderRadius:20, fontSize:10, fontWeight:700, padding:'2px 8px',
                      background:cat.bg, color:cat.color,
                      border:`1px solid ${cat.color}33`,
                    }}>
                      {cat.label}
                    </Tag>
                  </div>

                  {/* Level */}
                  <div style={{ padding:'10px 8px', display:'flex', alignItems:'center' }}>
                    <Tag style={{
                      borderRadius:20, fontSize:10, fontWeight:700, padding:'2px 8px',
                      background:lvl.bg, color:lvl.color,
                      border:`1px solid ${lvl.color}33`,
                    }}>
                      {lvl.icon} {lvl.label}
                    </Tag>
                  </div>

                  {/* Message */}
                  <div style={{ padding:'10px 8px' }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text1,
                      lineHeight:1.4 }}>
                      {log.message}
                    </div>
                    {log.detail && showDetail?.id === log.id && (
                      <div style={{
                        marginTop:8, padding:'8px 12px',
                        background:C.surf, borderRadius:8,
                        border:`1px solid ${C.border}`,
                        fontSize:11, fontFamily:'monospace',
                        color:C.text2, whiteSpace:'pre-wrap',
                        maxHeight:120, overflowY:'auto',
                      }}>
                        {typeof log.detail === 'object'
                          ? JSON.stringify(log.detail, null, 2)
                          : log.detail}
                      </div>
                    )}
                    {log.detail && showDetail?.id !== log.id && (
                      <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>
                        Click to see detail ›
                      </div>
                    )}
                  </div>

                  {/* Source */}
                  <div style={{ padding:'10px 8px', display:'flex', alignItems:'center' }}>
                    <span style={{ fontSize:10, color:C.text3, fontWeight:600,
                      background:C.surf, padding:'2px 8px', borderRadius:20,
                      border:`1px solid ${C.border}` }}>
                      {log.source || 'frontend'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Legend ── */}
        <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginTop:16 }}>
          <span style={{ fontSize:12, color:C.text3, fontWeight:600 }}>Categories:</span>
          {Object.values(CATEGORY).map(cat => (
            <span key={cat.label} style={{
              fontSize:11, fontWeight:600,
              color:cat.color, background:cat.bg,
              padding:'2px 10px', borderRadius:20,
              border:`1px solid ${cat.color}33`,
            }}>
              {cat.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}